import { useState } from "react";
import {
  Apple,
  ChevronDown,
  Clipboard,
  ClipboardCheck,
  Google,
  Microsoft,
  Sliders,
} from "react-bootstrap-icons";
import { cn } from "@utils";
import { useCalendarFeed } from "../hooks/useCalendar";

const CopyButton = ({ value, label = "Copy link" }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-zinc-700"
    >
      {copied ? <ClipboardCheck className="size-3.5" /> : <Clipboard className="size-3.5" />}
      {copied ? "Copied!" : label}
    </button>
  );
};

const Steps = ({ items }) => (
  <ol className="list-decimal space-y-1 pl-4 text-xs text-zinc-600 marker:text-zinc-400">
    {items.map((step, i) => (
      <li key={i}>{step}</li>
    ))}
  </ol>
);

const AppRow = ({ icon: Icon, name, hint, expanded, onToggle, children }) => (
  <div className="border-t border-zinc-100 first:border-t-0">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="flex w-full items-center gap-2.5 rounded-md px-2 py-2.5 text-left hover:bg-zinc-50"
    >
      <Icon className="size-4 shrink-0 text-zinc-500" />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-zinc-900">{name}</span>
        {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
      </span>
      <ChevronDown
        className={cn("size-4 shrink-0 text-zinc-400 transition-transform", expanded && "rotate-180")}
      />
    </button>
    {expanded && <div className="pb-3 pl-9 pr-2">{children}</div>}
  </div>
);

const AdvancedLink = ({ label, description, value }) => (
  <div>
    <p className="text-xs font-medium text-zinc-700">{label}</p>
    <p className="text-xs text-zinc-500">{description}</p>
    <CopyButton value={value} label="Copy" />
  </div>
);

// The "add my calendar to an external app" flow (ICS / webcal / CalDAV), as a section that can sit
// inside a larger menu. Self-contained — resolves the user's own feed links internally.
const SubscribeSection = () => {
  const { data: feed, isLoading } = useCalendarFeed();
  const [open, setOpen] = useState(null);
  const toggle = (key) => setOpen((cur) => (cur === key ? null : key));

  return (
    <div>
      <div className="px-2 pb-2 pt-1">
        <p className="text-sm font-medium text-zinc-900">Add my calendar to another app</p>
        <p className="mt-0.5 text-xs text-zinc-500">
          See these bookings in the calendar you already use. Pick your app for step-by-step
          instructions.
        </p>
      </div>
      {isLoading || !feed ? (
        <p className="px-2 py-2 text-sm text-zinc-500">Loading…</p>
      ) : (
        <div>
          <AppRow
            icon={Google}
            name="Google Calendar"
            hint="On a computer"
            expanded={open === "google"}
            onToggle={() => toggle("google")}
          >
            <Steps
              items={[
                "Open Google Calendar on a computer.",
                'Next to "Other calendars", click + then "From URL".',
                "Paste the link below and click Add calendar.",
              ]}
            />
            <CopyButton value={feed.icsUrl} />
          </AppRow>

          <AppRow
            icon={Apple}
            name="Apple Calendar"
            hint="iPhone, iPad or Mac"
            expanded={open === "apple"}
            onToggle={() => toggle("apple")}
          >
            <p className="mb-1 text-xs font-medium text-zinc-700">On iPhone or iPad</p>
            <Steps
              items={[
                "Copy the link below.",
                "Open Settings → Calendar → Accounts → Add Account → Other.",
                'Tap "Add Subscribed Calendar" and paste the link.',
              ]}
            />
            <p className="mb-1 mt-3 text-xs font-medium text-zinc-700">On a Mac</p>
            <Steps
              items={[
                "Open the Calendar app.",
                "Choose File → New Calendar Subscription.",
                "Paste the link below and click Subscribe.",
              ]}
            />
            <CopyButton value={feed.webcalUrl} />
          </AppRow>

          <AppRow
            icon={Microsoft}
            name="Outlook"
            hint="Web, Mac or Windows"
            expanded={open === "outlook"}
            onToggle={() => toggle("outlook")}
          >
            <p className="mb-1 text-xs font-medium text-zinc-700">
              Outlook on the web, or new Outlook (Mac &amp; Windows)
            </p>
            <Steps
              items={[
                'In the Calendar, click "Add calendar".',
                'In the list on the left, choose "Subscribe from web"',
                "Paste the link below, give it a name, then click Import.",
              ]}
            />
            <p className="mb-1 mt-3 text-xs font-medium text-zinc-700">
              Classic Outlook for Windows
            </p>
            <Steps
              items={[
                "Go to File → Account Settings → Account Settings.",
                'On the "Internet Calendars" tab, click New, paste the link and click Add.',
              ]}
            />
            <CopyButton value={feed.icsUrl} />
          </AppRow>

          <AppRow
            icon={Sliders}
            name="Other app or advanced"
            hint="Raw links, including two-way sync"
            expanded={open === "advanced"}
            onToggle={() => toggle("advanced")}
          >
            <div className="space-y-3">
              <AdvancedLink
                label="Webcal link"
                description="Read-only. Works in most calendar apps."
                value={feed.webcalUrl}
              />
              <AdvancedLink
                label="ICS link (https)"
                description="Read-only. Use if webcal isn't supported."
                value={feed.icsUrl}
              />
              <AdvancedLink
                label="CalDAV account URL"
                description="Two-way sync via a CalDAV account."
                value={feed.calDavUrl}
              />
            </div>
          </AppRow>
        </div>
      )}
      <p className="mt-1 border-t border-zinc-100 px-2 pb-1 pt-2 text-xs text-zinc-400">
        Anyone with these links can see your calendar — treat them like a password.
      </p>
    </div>
  );
};

export default SubscribeSection;
